import { ApnPdeApi } from "apis/ApnPdeApi";
import axios, { AxiosResponse } from "axios";
import { FileType } from "components/FileUploader/FileUploader";
import { FormatType } from "components/ImportWizard/ImportWizard";
import * as DateFns from "date-fns";
import { DataResponse } from "models/common/responseObjects";
import { ApnPdeStockResult } from "models/dbModels/apnPdes/ApnPdeStockResult";
import { ApnPdeSearchFilterQuery } from "models/filters/apnPde/ApnPdeSearchFilterQuery";
import { ManagedStockSearchFilterQueryLoadDetails } from "models/filters/managedStocks/ManagedStockSearchFilterQuery";
import { ChangeEvent } from "react";
import * as XLSX from "xlsx";

export type Row = any[];

export type DataSet = {
    [index: string]: XLSX.WorkSheet;
};

export interface CommonImportResponse<T> {
    data: T,
    errors: any[]
}

export function LoadSuppliers(currentStoreGroupId: number | null): Promise<CommonImportResponse<string[]>> {

    let functionResponse: CommonImportResponse<string[]> = {
        errors: [],
        data: [],
    }

    return axios.all([
        axios.post<any>(`api/v1/products/SearchSuppliers`, 
            { storeGroupId: currentStoreGroupId },
            { headers: {
                "Content-Encoding": "gzip"
            }
        })
    ]).then(axios.spread((...responses) => {

        const siteResults: AxiosResponse<DataResponse<string[]>> = responses[0];
        functionResponse.data = siteResults.data.data;

        return functionResponse;
    }))
        .catch((error) => {
            functionResponse.errors = [error?.toString()];
            return functionResponse;
        })
}

export async function HandleFile(ev: ChangeEvent<HTMLInputElement>): Promise<{ workbook: DataSet, sheets: string[] }> {
    const file = await ev.target.files?.[0]?.arrayBuffer();
    const data = XLSX.read(file);

    return {
        workbook: data.Sheets,
        sheets: data.SheetNames
    }
}

export async function HandleFile2(files: FileType[]): Promise<{ workbook: DataSet, sheets: string[], format: FormatType }> {
    const file = await files?.[0]?.arrayBuffer();
    const data = XLSX.read(file);

    const regexDate = /^\d{1,2}\/\d{1,2}\/\d{2,4}$/;
    //convert all math notation to normal expanded numbers (ie 1.23E+12 to 1230000000000)
    for (const sheetName of data.SheetNames) {
        const sheet = data.Sheets[sheetName];
        for (const cell in sheet) {
            if (sheet[cell].t === 'n' && !regexDate.test(sheet[cell].w)) { //Make sure the number is not a date though since they are stored as numbers
                sheet[cell].w = sheet[cell].v.toString();
            }            
        }
    }

    return {
        workbook: data.Sheets,
        sheets: data.SheetNames,
        format: data.Props === undefined ? 'csv' : 'xls',
    }
}

export async function DropHandler(ev: any): Promise<{ workbook: DataSet, sheets: string[] } | undefined> {
    console.log("File(s) dropped");

    // Prevent default behavior (Prevent file from being opened)
    ev.preventDefault();

    if (ev.dataTransfer.items) {
        // Use DataTransferItemList interface to access the file(s)
        for (let i = 0; i < ev.dataTransfer.items.length; i++) {
            // If dropped items aren"t files, reject them
            if (ev.dataTransfer.items[i].kind === "file") {
                const file = await ev.dataTransfer.items[i].getAsFile().arrayBuffer();
                const data = XLSX.read(file);

                return {
                    workbook: data.Sheets,
                    sheets: data.SheetNames
                }
            }
        }
    } else {
        // Use DataTransfer interface to access the file(s)
        for (let i = 0; i < ev.dataTransfer.files.length; i++) {
            console.log("... file[" + i + "].name = " + ev.dataTransfer.files[i].name);
        }
    }
}

export function setReadColumns(name: string, workBook: DataSet, headerRow: number = 1) {
    let columnOptions: any[] = [];
    let gridColumns: any[] = [];
    let gridRows: any[] = [];
    const rows: Row[] = XLSX.utils.sheet_to_json(workBook[name], { header: 1, defval: null, raw: false });
    for (let index = 0; index < rows.length; index++) {
        const row: any = rows[index];

        if (index === 0) {
            row.forEach((column: any, index: number) => {
                // console.log("name:" + column, ", index:", index );
                gridColumns.push({ Header: "Column " + index, accessor: "col" + index, columnName: column });
                columnOptions.push({ index: index, name: `Column ${index}` });
            });
        }

        if (index < headerRow) continue;

        let rowValues: any = {
            processed: null,
            message: null,
            stockId: null,
        };
        row.forEach((column: any, index: number) => { rowValues["col" + index] = column; });
        gridRows.push(rowValues);
    }
    return { columns: gridColumns, rows: gridRows, columnOptions: columnOptions };
}

export function CleanValue(value: string) {
    if (value == undefined || value == null) return value;

    return value.replaceAll(" ", "").replaceAll("\t", "");
}


export function GetStockById(stockIds: string[]): Promise<CommonImportResponse<any>> {

    let functionResponse: CommonImportResponse<any> = {
        errors: [],
        data: []
    }

    return axios.post<DataResponse<any>>(`api/v1/managedStocks/search`,
        {
            ids: stockIds,
            loadDetails: [ManagedStockSearchFilterQueryLoadDetails.Apns, ManagedStockSearchFilterQueryLoadDetails.Pdes],
        },
        { headers: { "Content-Encoding": "gzip" } },
    ).then(result => {
        functionResponse.data = result.data.data;
        return functionResponse;
    }).catch((error) => {
        functionResponse.errors = [error];
        return functionResponse;
    });
}

export function SearchForDuplicates(filters: ApnPdeSearchFilterQuery): Promise<CommonImportResponse<ApnPdeStockResult[]>> {
    let functionResponse: CommonImportResponse<ApnPdeStockResult[]> = {
        errors: [],
        data: []
    }

    return ApnPdeApi.Search(filters)
        .then(result => {
            functionResponse.data = result.data.data;
            return functionResponse;
        }).catch((error) => {
            functionResponse.errors = [error];
            return functionResponse;
        })
}

export function ParseDate(input: string): string | null {
    let formats = ["dd/MM/yy", "d/M/yy", "dd/MM/yyyy", "d/M/yyyy"];

    for (let index = 0; index < formats.length; index++) {
        const format = formats[index];
        let result: any = undefined;
        try {
            result = DateFns.format(DateFns.parse(input, format, new Date()), "yyyy-MM-dd");
        } catch {
        }

        if (result) {
            return result
        }
    }
    return null;
}


export function ParseNumber(input: string): number | null {
    var number = Number(input);
    if (isNaN(number)) {
        return null;
    }
    return number;
}

